import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getSharedFormStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';

import { editExpirationDatesAction } from 'store/loanApplications/actions';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import { getGeneralConfig } from './GeneralEdit.config';

const GeneralForm = ({
  loanId,
  applicationData,
  onCancelForm,
  pendingChangesData,
  isLoading,
  setIsLoading,
  isEcdHidden,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedFormStyles(theme), [theme]);

  const { initialConfig, schema, fieldsConfig, initialFormValues } = useMemo(
    () =>
      getConfigInfo({
        applicationData: {
          manuallyUnderwritten: false,
          ...applicationData,
        },
        config: getGeneralConfig({ isEcdHidden }),
      }),
    [applicationData, isEcdHidden],
  );
  const { changedFields } = useMemo(
    () => getChangedFieldsByConfig({ pendingChangesData, initialConfig }),
    [pendingChangesData, initialConfig],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    validateField,
    isFormChanged,
  } = useForm(initialFormValues, fieldsConfig);

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      setIsLoading(true);

      const valuesToSend = {
        loanId,
        manuallyUnderwritten: form.manuallyUnderwritten,
        requestMetadata: { tabId: LoanApplicationTabId.KEY_DATES },
      };

      dispatch(editExpirationDatesAction(valuesToSend))
        .then(() => onCancelForm())
        .finally(() => {
          setIsLoading(false);
        });
    },
    [form.manuallyUnderwritten, dispatch, loanId, onCancelForm, setIsLoading],
  );

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  return (
    <form
      noValidate
      className="mt-52"
      data-testid={UnderwritingTestIds.GENERAL_FORM}
      onSubmit={onSubmit}
    >
      <div className="flex justify-end">
        <Button
          color="primary"
          data-testid={UnderwritingTestIds.GENERAL_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading}
          sx={sharedClasses.submitButton}
          type="submit"
          variant="contained"
        >
          Done
        </Button>
        <Button
          data-testid={UnderwritingTestIds.GENERAL_FORM_CANCEL_BUTTON}
          sx={sharedClasses.cancelButton}
          type="button"
          variant="contained"
          onClick={onCancelForm}
        >
          Cancel
        </Button>
      </div>

      <FormGenerator
        changedFields={changedFields}
        config={initialConfig}
        form={form}
        formName={FormNames.UNDERWRITING_GENERAL}
        handleChange={handleChange}
        isLoading={isLoading}
        numberOfColumns={3}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </form>
  );
};

GeneralForm.propTypes = {
  ...WithLoadingPropTypes,
  applicationData: PropTypes.shape({
    earliestClosingDate: PropTypes.string,
    manuallyUnderwritten: PropTypes.bool,
  }).isRequired,
  pendingChangesData: PropTypes.object.isRequired,
  isEcdHidden: PropTypes.bool.isRequired,
  onCancelForm: PropTypes.func.isRequired,
};

export default GeneralForm;
